import { memo, useEffect, useMemo, useRef, useSyncExternalStore, type ReactElement, type RefObject } from "react";
import { CURRENCY_COIN, effectByNumber, xpRatesOf } from "odyssey-shared";
import { Glyph, IconButton, KeyGlyph, logical, useLogicalSize, type IconArt } from "../../ui/kit/index.ts";
import type { HudArea, SlotRect } from "../hud/slots.ts";
import { xpTip } from "../hud/xp-rate.ts";
import { stanceTip } from "../hud/stance-words.ts";
import { itemGlyph } from "../bag/describe.ts";
import { ITEM_CARD, ItemCard } from "../item-card/card.tsx";
import { itemCard, type ItemCardContent } from "../item-card/card-words.ts";
import { itemArt, useIcons, type IconsView } from "../icons/context.tsx";
import { bagBoxAt, bagTipPlace } from "../bag/layout.ts";
import type { Rules } from "odyssey-shared";
import type { BagSnapshot } from "../bag/store.ts";
import { measureBagSlot, measureTipAnchor } from "../bag/measure.ts";
import { walletMarkName, walletTipAnchor } from "../wallet/describe.ts";
import { CARRY_TIP_ANCHOR } from "../bag/carry.ts";
import { NAME_TIP_ANCHOR } from "../hud/name-tip.ts";
import { wordsOf, wordsTipAnchor } from "./hover-words.tsx";
import { abilityAction } from "../controls/actions.ts";
import { pageKeys } from "../controls/page.ts";
import { windowPlace, windowSize } from "../windows/places.ts";
import type { Stance } from "../fight/stance.ts";
import { abilityTip, effectTip, emptyTip, tipGroups, type HudTip } from "./describe.ts";
import type { BarSnapshot, BarSource } from "./store.ts";
import { NO_BAG, NO_PLACE, NO_SIZE, NO_STANCE, NO_SUBSCRIBE, NO_WALLET, type BagSubject, type StanceSubject, type XpSubject } from "./tooltip-subjects.ts";
import type { BarTicker } from "./ticker.ts";
import { ConsumedPress, type PressEvent } from "./consume.ts";
import { TIP_GEOMETRY, labelTipPlace, tipPlace, type TipPlace } from "./tip-place.ts";
import { anchorOf } from "./tip-handlers.ts";
import type { TipAnchor, TipController } from "./tips.ts";
import { say } from "../../words.ts";

/**
 * The bar's and the effects rows' tooltip on the page (P2.2-T12, ADR-061's and ADR-079's amendments of 2026-10-02):
 * the handlers the bar and the rows take, delegated on their containers so no slot or icon carries its own, which hand
 * the pointer, focus and click to the `TipController`; and the one tooltip, the design's `RichTooltip` in the atlas's
 * `tooltip` frame, drawn for the anchor the controller shows, or for a slot of the Inventory window or an item slot of
 * the bar the item card (P2.8-T46 and P2.8-T55, ADR-125 decision 5 and its note for P2.8-T55) in its place, or for a
 * mark of the Inventory window's wallet line its label (P2.8-T67, ADR-124's note for P2.8-T67). It is built
 * only when that anchor or its subject changes; an effect's time left and an item slot's card's time left are written
 * by the bar's ticker.
 */

/**
 * The tooltip's element id, which the slot or icon it is open for names as its description while it is open.
 */
export const TIP_ID = "ot-hud-tip";

/**
 * The pinned tooltip's close button's accessible name (a draft for the author).
 */
const TIP_CLOSE_LABEL = "Close";

/**
 * The events the window hands the consumed press's filter (`ConsumedPress`): the game reads mouse and touch events,
 * so each of the press's own stops at the window before the canvas hears it.
 */
const CONSUMED_EVENTS = ["pointerup", "pointercancel", "pointermove", "mousedown", "mouseup", "mousemove", "touchstart", "touchmove", "touchend", "touchcancel"] as const;

/**
 * An event as the consumed press's filter reads it.
 *
 * @param event The event.
 * @returns Its type, a pointer's id and a touch event's changed identifiers.
 */
function pressEventOf(event: Event): PressEvent {
  if (typeof TouchEvent !== "undefined" && event instanceof TouchEvent) {
    return { type: event.type, touches: Array.from(event.changedTouches, (touch) => touch.identifier) };
  }
  if (event instanceof globalThis.PointerEvent) {
    return { type: event.type, pointerId: event.pointerId };
  }
  return { type: event.type };
}

/**
 * The width the empty slot's label tooltip is held inside the area by, in logical pixels.
 */
const LABEL_WIDTH = 60;

/**
 * A bag slot's or a bar item slot's item card: the item's rules id, which its picture is looked up by, and the card's
 * content.
 */
interface CardTip {
  readonly kind: "card";
  readonly item: string;
  readonly content: ItemCardContent;
}

/**
 * Any tooltip the view draws: the bar's, the rows', the XP bar's and the stance button's, and the item card of a bag
 * slot or a bar item slot.
 */
type ShownTip = HudTip | CardTip;

/**
 * The tooltip an anchor shows from what the bar draws now, or null when its slot or icon is gone. A slot's and the
 * stance's tooltip name the key the page's key table binds them to (P1.9-T50, `controls/`), read as the tooltip is
 * worked out; the table changes only in Settings, which no tooltip shows over.
 *
 * @param source The bar's source, whose rules draft the words.
 * @param bar What the bar draws.
 * @param anchor The anchor.
 * @param xp The player's progress and flag byte, which the XP bar's tooltip reads, or null before the first progress.
 * @param stance The stance the stance button's tooltip reads, or null where the HUD has none.
 * @param bag The bag a bag slot's card reads, or null where the HUD has none.
 * @param bagRules The rules the items are drawn by, on a bag slot's card and a bar item slot's alike.
 * @param halved Whether a consumable's heal is halved now (in combat with a player, the `vitals` bit `InPvpCombat`), which an
 *   item's card reads.
 * @param copper The purse the open Inventory window's wallet line draws, in copper, or null where it draws none.
 * @param cardName The player card's full name, which its name's tooltip reads, empty before the character is known.
 * @returns The tooltip: for a bar item slot the item card naming the slot's count, the total carried (ADR-121
 *   decision 9.2); for a wallet mark or the carry number its label.
 */
function tipOf(source: BarSource, bar: BarSnapshot, anchor: TipAnchor, xp: XpSubject | null, stance: Stance | null, bag: BagSnapshot | null, bagRules: Rules, halved: boolean, copper: number | null, cardName: string): ShownTip | null {
  if (anchor.surface === "name") {
    return cardName === "" || anchor.index !== 0 ? null : { kind: "label", name: cardName };
  }
  if (anchor.surface === "words") {
    const words = wordsOf(anchor.index);
    return words === null ? null : { kind: "label", name: words };
  }
  if (anchor.surface === "wallet") {
    const name = copper === null ? null : walletMarkName(copper, anchor.index);
    return name === null ? null : { kind: "label", name };
  }
  if (anchor.surface === "carry") {
    return bag?.open === true && anchor.index === 0 ? { kind: "label", name: say("bag.carry.tip") } : null;
  }
  if (anchor.surface === "bag") {
    const slot = bag?.open === true ? bag.slots[anchor.index] : undefined;
    const item = slot?.item ?? null;
    const content = slot === undefined || item === null ? null : itemCard(bagRules, item, slot.quantity, halved);
    return item === null || content === null ? null : { kind: "card", item, content };
  }
  if (anchor.surface === "stance") {
    return stance === null ? null : stanceTip(stance, pageKeys.label("stance"));
  }
  if (anchor.surface === "xp") {
    return xp === null ? null : xpTip(source.rules.tables.levels, xp.progress, xp.flag, xpRatesOf(source.rules));
  }
  if (anchor.surface === "slot") {
    const slot = bar.slots[anchor.index];
    if (slot === undefined) {
      return null;
    }
    if (slot.item !== null) {
      const content = itemCard(bagRules, slot.item, slot.count ?? 1, halved);
      return content === null ? null : { kind: "card", item: slot.item, content };
    }
    const ability = source.rules.tables.abilities[slot.ability - 1];
    const key = pageKeys.label(abilityAction(slot.index));
    return ability === undefined ? emptyTip(key) : abilityTip(source.rules, ability, key);
  }
  const view = (anchor.surface === "own" ? bar.own : bar.target)[anchor.index];
  const effect = view === undefined ? undefined : effectByNumber(source.rules, view.effect);
  const row = anchor.surface === "own" ? source.times.own : source.times.target;
  return view === undefined || effect === undefined ? null : effectTip(source.rules, effect, view.stacks, row.effect[anchor.index] === view.effect ? (row.span[anchor.index] as number) : 0);
}

/**
 * What decides an anchor's tooltip, so it is built again only when that changes: the slot's ability, or the item
 * slot's item, its count and whether a consumable's heal is halved now, the icon's
 * effect and stacks, the XP bar's level, XP and flag, a bag slot's item, its count and whether the heal is halved, or
 * a wallet mark's purse, or the carry number's open window.
 *
 * @param bar What the bar draws.
 * @param anchor The anchor, or null.
 * @param xp The player's progress and flag byte, or null.
 * @param stance The stance, or null.
 * @param bag The bag, or null.
 * @param halved Whether a consumable's heal is halved now.
 * @param copper The purse the open Inventory window's wallet line draws, in copper, or null where it draws none.
 * @param cardName The player card's full name, which its name's tooltip reads.
 * @returns A key, empty for no subject.
 */
export function subjectKey(bar: BarSnapshot, anchor: TipAnchor | null, xp: XpSubject | null, stance: Stance | null, bag: BagSnapshot | null, halved: boolean, copper: number | null = null, cardName = ""): string {
  if (anchor === null) {
    return "";
  }
  if (anchor.surface === "name") {
    return cardName === "" || anchor.index !== 0 ? "" : `name:0:${cardName}`;
  }
  if (anchor.surface === "words") {
    const words = wordsOf(anchor.index);
    return words === null ? "" : `words:${anchor.index}:${words}`;
  }
  if (anchor.surface === "wallet") {
    return copper === null ? "" : `wallet:${anchor.index}:${copper}`;
  }
  if (anchor.surface === "carry") {
    return bag?.open === true ? `carry:${anchor.index}` : "";
  }
  if (anchor.surface === "bag") {
    const slot = bag?.open === true ? bag.slots[anchor.index] : undefined;
    return slot === undefined || slot.item === null ? "" : `bag:${anchor.index}:${slot.item}:${slot.quantity}:${halved}`;
  }
  if (anchor.surface === "stance") {
    return stance === null ? "" : `stance:${stance}`;
  }
  if (anchor.surface === "xp") {
    return xp === null ? "" : `xp:${xp.progress.level}:${xp.progress.xp}:${xp.flag}`;
  }
  if (anchor.surface === "slot") {
    const slot = bar.slots[anchor.index];
    return slot === undefined ? "" : slot.item === null ? `slot:${anchor.index}:${slot.ability}` : `slot:${anchor.index}:${slot.item}:${slot.count ?? 1}:${halved}`;
  }
  const view = (anchor.surface === "own" ? bar.own : bar.target)[anchor.index];
  return view === undefined ? "" : `${anchor.surface}:${anchor.index}:${view.effect}:${view.stacks}`;
}

/**
 * Where an anchor's tooltip stands: a bag slot's beside the window or by its measured slot, a wallet mark's or the carry number's above or
 * below its measured mark, any other's from the HUD's layout.
 *
 * @param anchor The anchor.
 * @param area The HUD's area.
 * @param targetLeft The target row's left edge.
 * @param width The tooltip's width.
 * @param height The tooltip's laid-out height, 0 before it is laid out.
 * @param bagBox The Inventory window's box as drawn.
 * @returns The place, or null for a wallet mark or a carry number not drawn on the page.
 */
function placeOf(anchor: TipAnchor, area: HudArea, targetLeft: number, width: number, height: number, bagBox: SlotRect): TipPlace | null {
  if (anchor.surface === "bag") {
    return bagTipPlace(measureBagSlot(anchor.index), area, width, bagBox, height);
  }
  if (anchor.surface === "wallet" || anchor.surface === "carry" || anchor.surface === "name" || anchor.surface === "words") {
    const mark = measureTipAnchor(anchor.surface === "wallet" ? walletTipAnchor(anchor.index) : anchor.surface === "carry" ? CARRY_TIP_ANCHOR : anchor.surface === "words" ? wordsTipAnchor(anchor.index) : NAME_TIP_ANCHOR);
    return mark === null ? null : labelTipPlace(mark, area, width, height);
  }
  return tipPlace(anchor, area, targetLeft, width);
}

/**
 * The tooltip's body: an empty slot's label with its key, or the rich tooltip: the name (with a slot's key), the type
 * line (with an effect's time left, which the ticker writes into `time`), a slot's costs, reach and cooldown, and the
 * lines in their tones, grouped in the design's blocks (`tipGroups`). A pinned tooltip ends its head with the kit's
 * close button (the author's ruling of 2026-10-02).
 *
 * @param props The `tip`, the `time` ref the time left is written into, and `onClose`, the close button's action, or
 *   null for a tooltip that is not pinned.
 * @returns The body.
 */
function TipBody({ tip, time, onClose }: { readonly tip: HudTip; readonly time: RefObject<HTMLSpanElement | null>; readonly onClose: (() => void) | null }): ReactElement {
  const close = onClose === null ? null : <TipClose onClose={onClose} />;
  if (tip.kind === "label") {
    return (
      <span className="ot-hud-tip__head">
        <span className="ot-hud-tip__label" data-hud="tip-label">
          {tip.name}
        </span>
        {close}
      </span>
    );
  }
  if (tip.kind === "empty") {
    return (
      <span className="ot-hud-tip__head">
        <span className="ot-hud-tip__label">{tip.name}</span>
        <KeyGlyph>{tip.key}</KeyGlyph>
        {close}
      </span>
    );
  }
  return (
    <>
      <span className="ot-hud-tip__head">
        <span className="ot-hud-tip__name" data-hud="tip-name">
          {tip.name}
        </span>
        <span className="ot-hud-tip__marks">
          {tip.kind === "ability" || tip.kind === "stance" ? <KeyGlyph>{tip.key}</KeyGlyph> : null}
          {close}
        </span>
      </span>
      <span className="ot-hud-tip__type">
        {tip.typeLine}
        {tip.kind === "effect" ? (
          <>
            {" · "}
            <span ref={time} data-hud="tip-time" />
            {" left"}
          </>
        ) : null}
      </span>
      {tip.kind === "ability" ? (
        <span className="ot-hud-tip__stats" data-hud="tip-stats">
          {tip.costs.map((cost) => (
            <span key={cost.resource} className={`ot-hud-tip__cost is-${cost.resource}`}>
              {cost.text}
            </span>
          ))}
          <span>{tip.range}</span>
          <span className="ot-hud-tip__cooldown">{tip.cooldown}</span>
        </span>
      ) : null}
      {tipGroups(tip.lines).map((group) => (
        <span key={group[0]?.text} className="ot-hud-tip__group">
          {group.map((line) => (
            <span key={line.text} className={`ot-hud-tip__line is-${line.tone}`} data-hud="tip-line">
              {line.text}
            </span>
          ))}
        </span>
      ))}
    </>
  );
}

/**
 * The pinned tooltip's close button, the only part of a tooltip that takes the pointer.
 *
 * @param props `onClose`, its action.
 * @returns The button in its holder.
 */
function TipClose({ onClose }: { readonly onClose: () => void }): ReactElement {
  return (
    <span className="ot-hud-tip__close" data-hud="tip-close">
      <IconButton icon="close" label={TIP_CLOSE_LABEL} onClick={onClose} />
    </span>
  );
}

/**
 * The bar's item card's "Ready in" row (ADR-125's note for P2.8-T55): drawn like a stat row, with the time left the
 * bar's ticker writes into `time` and the cooling mark it sets on `row` while the item cools; unmarked, the card's
 * style sheet leaves it out.
 *
 * @param props The `row` and `time` refs the ticker writes.
 * @returns The row.
 */
function ReadyRow({ row, time }: { readonly row: RefObject<HTMLSpanElement | null>; readonly time: RefObject<HTMLSpanElement | null> }): ReactElement {
  return (
    <span ref={row} className="ot-item-card__stat is-ready-in" data-hud="card-ready">
      <Glyph name="hourglass" />
      <span className="ot-item-card__label">{say("bar.readyIn")}</span>
      <span ref={time} className="ot-item-card__value" data-hud="card-ready-time" />
    </span>
  );
}

/**
 * The footer's hint on the item card (ADR-125 decision 5.2 and its note for P2.8-T55): "Right click for actions" on a
 * bag slot's card on a page with a fine pointer; none on one with touch alone, which has no right click, and none on a
 * bar slot's, where right click does nothing.
 *
 * @param surface The anchor's surface.
 * @returns The hint, or null.
 */
function cardHint(surface: TipAnchor["surface"]): string | null {
  return surface !== "bag" || (typeof matchMedia === "function" && !matchMedia("(any-pointer: fine)").matches) ? null : say("bag.card.hint");
}

/**
 * The item's picture on its card: the art at the card's room (ADR-125 decision 5.3, k device pixels an art pixel, the
 * largest whole number that fits 48, which is at least 1 at every UI scale), or null where the glyph stands (ADR-111
 * decision 16: no icon, a key the pointer lacks, or no atlas).
 *
 * @param icons The tree's icons for this render.
 * @param rules The rules the item is read from.
 * @param item The item's rules id.
 * @returns The picture, or null for the glyph.
 */
function cardArt(icons: IconsView, rules: Rules, item: string): IconArt | null {
  return itemArt(icons, rules, item, ITEM_CARD.artRoom);
}

/**
 * The one tooltip. A press anywhere on the page tells the controller, which unpins a pinned tooltip on a press off its
 * slot or icon; such a press that lands on the world (the canvas) is consumed, its own events stopped at the window
 * before the game hears them (`ConsumedPress`, the lead's ruling of 2026-10-02 as narrowed), while one on any HUD
 * control closes the tooltip and does its own thing; a pinned one also carries a close button, the only part of it that takes the pointer, and a press on
 * the tooltip itself unpins nothing, so the press reaches that button; the slot or icon it is open for names it as its
 * description (`aria-describedby`) only while it is open, except a label whose anchor is already named by the same words
 * (a wallet mark's, the carry number's), so a screen reader reads them once; a tooltip whose slot or icon is gone (an effect ended, the target dropped) is cleared.
 *
 * @param props The `area` it is laid out on, the bar's `source`, the `tips` controller, the `ticker` that writes an
 *   effect's time left, the target row's left edge (`targetLeft`), what the XP bar's tooltip reads (`xp`, null
 *   before the first progress), the stance the stance button's tooltip reads (`stance`, P2.2-T13, absent where the
 *   HUD has no stance button), and what a bag slot's card reads (`bag`, P2.8-T11, absent where the HUD has no
 *   Inventory window). A bag slot's card is placed by its laid-out height, so it stays inside the area whatever its
 *   content; a bar item slot's stands up from the bar as the bar's tooltip does.
 * @returns The tooltip, or nothing.
 */
export const HudTooltip = memo(function HudTooltip({ area, source, tips, ticker, targetLeft, xp, stance = null, bag = null, cardName = "" }: { readonly area: HudArea; readonly source: BarSource; readonly tips: TipController; readonly ticker: BarTicker | null; readonly targetLeft: number; readonly xp: XpSubject | null; readonly stance?: StanceSubject | null; readonly bag?: BagSubject | null; readonly cardName?: string }): ReactElement | null {
  const shown = useSyncExternalStore(tips.subscribe, tips.getSnapshot, tips.getSnapshot);
  const anchor = shown?.anchor ?? null;
  const bar = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
  const stanceNow = useSyncExternalStore(stance?.subscribe ?? NO_SUBSCRIBE, stance?.getSnapshot ?? NO_STANCE, stance?.getSnapshot ?? NO_STANCE);
  const bagNow = useSyncExternalStore(bag?.source.subscribe ?? NO_SUBSCRIBE, bag?.source.getSnapshot ?? NO_BAG, bag?.source.getSnapshot ?? NO_BAG);
  const walletNow = useSyncExternalStore(bag?.wallet?.subscribe ?? NO_SUBSCRIBE, bag?.wallet?.getSnapshot ?? NO_WALLET, bag?.wallet?.getSnapshot ?? NO_WALLET);
  const copper = bagNow?.open === true ? (walletNow?.[CURRENCY_COIN] ?? null) : null;
  const bagPlaced = bag === null ? null : (bag.place ?? windowPlace("inventory"));
  const bagPlace = useSyncExternalStore(bagPlaced?.subscribe ?? NO_SUBSCRIBE, bagPlaced?.getSnapshot ?? NO_PLACE, bagPlaced?.getSnapshot ?? NO_PLACE);
  const bagSized = bag === null ? null : (bag.size ?? windowSize("inventory"));
  const bagSize = useSyncExternalStore(bagSized?.subscribe ?? NO_SUBSCRIBE, bagSized?.getSnapshot ?? NO_SIZE, bagSized?.getSnapshot ?? NO_SIZE);
  const halved = bag?.potionsHalved ?? false;
  const bagRules = bag?.rules ?? source.rules;
  const icons = useIcons();
  const key = subjectKey(bar, anchor, xp, stanceNow, bagNow, halved, copper, cardName);
  const time = useRef<HTMLSpanElement>(null);
  const readyRow = useRef<HTMLSpanElement>(null);
  const readyTime = useRef<HTMLSpanElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const tip = useMemo(() => (anchor === null || key === "" ? null : tipOf(source, bar, anchor, xp, stanceNow, bagNow, bagRules, halved, copper, cardName)), [anchor, key, source, bagRules]);
  const laid = useLogicalSize(box, tip);
  useEffect(() => {
    if (anchor !== null && tip === null) {
      tips.clear();
    }
  }, [anchor, tip, tips]);
  useEffect(() => {
    const consumed = new ConsumedPress();
    const filter = (event: Event): void => {
      if (consumed.active && consumed.stops(pressEventOf(event), performance.now())) {
        event.stopPropagation();
        event.preventDefault();
      }
    };
    const press = (event: globalThis.PointerEvent): void => {
      if (event.target instanceof Element && event.target.closest(`#${TIP_ID}`) !== null) {
        return;
      }
      if (tips.pressAnywhere(anchorOf(event.target)?.anchor ?? null) && event.target instanceof HTMLCanvasElement) {
        consumed.begin(event.pointerType, event.pointerId, performance.now());
        event.stopPropagation();
        event.preventDefault();
      }
    };
    const clear = (): void => consumed.clear();
    const capture = { capture: true, passive: false } as const;
    window.addEventListener("pointerdown", press, capture);
    for (const type of CONSUMED_EVENTS) {
      window.addEventListener(type, filter, capture);
    }
    window.addEventListener("blur", clear);
    document.addEventListener("visibilitychange", clear);
    return () => {
      window.removeEventListener("pointerdown", press, capture);
      for (const type of CONSUMED_EVENTS) {
        window.removeEventListener(type, filter, capture);
      }
      window.removeEventListener("blur", clear);
      document.removeEventListener("visibilitychange", clear);
    };
  }, [tips]);
  useEffect(() => {
    if (anchor === null || tip === null || tip.kind === "label") {
      return undefined;
    }
    const element = document.querySelector(`[data-tip="${anchor.surface}:${anchor.index}"]`);
    element?.setAttribute("aria-describedby", TIP_ID);
    return () => element?.removeAttribute("aria-describedby");
  }, [anchor, tip]);
  useEffect(() => {
    if (ticker === null || anchor === null || anchor.surface === "slot" || anchor.surface === "xp" || anchor.surface === "stance" || anchor.surface === "bag" || anchor.surface === "wallet" || anchor.surface === "carry" || anchor.surface === "name" || anchor.surface === "words" || tip === null || time.current === null) {
      return undefined;
    }
    ticker.bindTip(anchor.surface, anchor.index, time.current);
    return () => ticker.bindTip(null, -1, null);
  }, [ticker, anchor, tip]);
  useEffect(() => {
    if (ticker === null || anchor === null || anchor.surface !== "slot" || tip === null || tip.kind !== "card" || readyRow.current === null || readyTime.current === null) {
      return undefined;
    }
    ticker.bindSlotTip(anchor.index, readyRow.current, readyTime.current);
    return () => ticker.bindSlotTip(-1, null, null);
  }, [ticker, anchor, tip]);
  if (anchor === null || tip === null) {
    return null;
  }
  const width = tip.kind === "empty" ? LABEL_WIDTH : tip.kind === "label" ? Math.ceil(laid?.width ?? LABEL_WIDTH) : tip.kind === "card" ? ITEM_CARD.width : TIP_GEOMETRY.width;
  const place = placeOf(anchor, area, targetLeft, width, Math.ceil(laid?.height ?? 0), bagBoxAt(area, bagPlace, bagSize));
  if (place === null) {
    return null;
  }
  const onClose = shown?.pinned === true ? tips.close : null;
  const label = tip.kind === "empty" || tip.kind === "label";
  const frame = tip.kind === "card" ? " is-card" : ` ot-ui-frame-tooltip${label ? " is-label" : ""}`;
  return (
    <div ref={box} id={TIP_ID} role="tooltip" className={`ot-hud-tip${frame}`} data-hud="tip" data-tip-for={`${anchor.surface}:${anchor.index}`} style={{ left: logical(place.left), top: place.top === null ? undefined : logical(place.top), bottom: place.bottom === null ? undefined : logical(place.bottom), width: label ? undefined : logical(width) }}>
      {tip.kind === "card" ? <ItemCard content={tip.content} art={cardArt(icons, bagRules, tip.item)} glyph={itemGlyph(bagRules, tip.item)} hint={cardHint(anchor.surface)} close={onClose === null ? null : <TipClose onClose={onClose} />} extra={anchor.surface === "slot" ? <ReadyRow row={readyRow} time={readyTime} /> : null} /> : <TipBody tip={tip} time={time} onClose={onClose} />}
    </div>
  );
});
